import { Box } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import {
  CarouselProvider,
  Slide,
  Slider,
} from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";
import { useContext, useEffect, useMemo, useState } from "react";
import { useProducts } from "../../../API/Hooks/Products";
import { modeContext } from "../../../App";
import claroBanner from "../../../assets/img/clarobanner.png";
import cyclopay from "../../../assets/img/cyclopay.png";
import meeting from "../../../assets/img/meeting.png";
import { ProductCard } from "../../../components/ProductCard";
import { AuthContext } from "../../../contexts/Auth";
import { CompanyContext } from "../../../contexts/Company";
import { LoginMessageContext } from "../../../contexts/LoginMessage";
import { UserContext } from "../../../contexts/User";
import { loadItem } from "../../../utils/localStorage";
import {
  filterProductsByCarrier,
  groupProductsByCarrier,
} from "../../../utils/productsByCarrier";

const useStyles = makeStyles({
  rootMobile: {
    display: "flex",
    alignItems: "flex-start",
    backgroundColor: "#F7F9FB",
    flexWrap: "wrap",
    justifyContent: "center",
    minHeight: "calc(100vh - 160px)",
    padding: "24px 16px 40px",
  },
  rootDesktop: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    backgroundColor: "#F7F9FB",
    flexWrap: "wrap",
    padding: "32px 40px 48px",
  },
  rootDesktopDark: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    backgroundColor: "#474A56",
    flexWrap: "wrap",
    padding: "32px 40px 48px",
  },
  sessionTitleContainer: {
    backgroundColor: "#F7F9FB",
  },
  sessionTitleContainerDark: {
    backgroundColor: "#474A56",
  },
  content: {
    width: "100%",
    maxWidth: 1200,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 24,
  },
  grid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 40,
    width: "100%",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  emptyState: {
    width: "100%",
    padding: "40px 0",
    textAlign: "center",
    color: "#050821",
  },
  image: {
    width: "100%",
    objectFit: "cover",
    "&:hover": {
      cursor: "pointer",
    },
  },
  imageContainer: {
    width: "100%",
  },
});

const Home = () => {
  const classes = useStyles();

  const colorMode = useContext(modeContext);

  const { enqueueSnackbar } = useSnackbar();

  const [user] = useContext(UserContext);

  const [token] = useContext(AuthContext);

  const [login, dispatchLoginMessageContext] = useContext(LoginMessageContext);

  const [company] = useContext(CompanyContext);

  const isMobile = useMediaQuery("(max-width:900px)", { noSsr: true });

  const { fetchProducts, fetchUserProducts, requestData, requestLoading } =
    useProducts();

  const [products, setProducts] = useState({});

  const [reloaded, setReloaded] = useState(true);

  const [reloading, setReloading] = useState(false);

  const productList = products?.data || [];

  const productsByCarrier = useMemo(
    () => groupProductsByCarrier(productList),
    [productList]
  );

  const carriers = useMemo(
    () =>
      filterProductsByCarrier({
        productsByCarrier,
        selectedOperators: [],
        selectedPlans: [],
      }),
    [productsByCarrier]
  );

  useEffect(() => {
    const loadProducts = async () => {
      try {
        if (Object.keys(products)?.length === 0 && !loadItem("token")) {
          const result = await fetchProducts();
          if (result) setProducts(result);
        } else if (
          Object.keys(products)?.length === 0 &&
          loadItem("token") &&
          user.systemRole !== 4
        ) {
          const result = await fetchUserProducts();
          if (result) setProducts(result);
        } else if (
          Object.keys(products)?.length === 0 &&
          user.systemRole === 4 &&
          company?.company_id === undefined
        ) {
          const result = await fetchProducts();
          if (result) setProducts(result);
        } else if (
          Object.keys(products)?.length === 0 &&
          user.systemRole === 4 &&
          company?.company_id !== undefined
        ) {
          const result = await fetchUserProducts(company.company_id);
          if (result) setProducts(result);
        }
      } catch {
        setProducts({ data: [] });
      }
    };
    loadProducts();

    const loadSpecificProducts = async () => {
      try {
        if (
          loadItem("token") &&
          user.systemRole === 4 &&
          company?.company_id !== undefined &&
          company.companyChanged
        ) {
          const result = await fetchUserProducts(company.company_id);
          if (result) {
            setProducts(result);
          }
        }
      } catch {
        setProducts({ data: [] });
      }
    };
    loadSpecificProducts();

    if (!isMobile && !reloaded) {
      setReloading(true);
      window.location.reload();
    }

    if (isMobile) {
      setReloaded(false);
    }

    if (login.canShow === true) {
      dispatchLoginMessageContext({
        type: "setLoginMessage",
        canShow: false,
      });
      enqueueSnackbar(`Bem-vindo (a), ${user.userName}!`, {
        variant: "success",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    requestData,
    products,
    user,
    login,
    token,
    isMobile,
    reloaded,
    reloading,
    company,
  ]);

  const isDarkDesktop = !isMobile && colorMode === "dark";

  return (
    <>
      <div
        className={
          isDarkDesktop
            ? classes.sessionTitleContainerDark
            : classes.sessionTitleContainer
        }
      >
        <CarouselProvider
          naturalSlideWidth={3}
          naturalSlideHeight={0.5}
          totalSlides={3}
          visibleSlides={1}
          isPlaying={true}
          interval={4000}
        >
          <Slider>
            <Slide>
              <div className={classes.imageContainer}>
                <img className={classes.image} src={cyclopay} alt="Claro" />
              </div>
            </Slide>
            <Slide>
              <img className={classes.image} src={claroBanner} alt="Sala" />
            </Slide>
            <Slide>
              <img className={classes.image} src={meeting} alt="Cyclopay" />
            </Slide>
          </Slider>
        </CarouselProvider>
      </div>
      <div
        className={
          isMobile
            ? classes.rootMobile
            : isDarkDesktop
            ? classes.rootDesktopDark
            : classes.rootDesktop
        }
      >
        {!reloading && (
          <div className={classes.content}>
            <Box className={classes.grid}>
              {carriers.map(({ carrier, availablePlans, isMulti }) => (
                <ProductCard
                  key={`carrier-card-${carrier}`}
                  carrier={carrier}
                  availablePlans={availablePlans}
                  isMulti={isMulti}
                />
              ))}
            </Box>

            {!requestLoading && carriers.length === 0 ? (
              <div className={classes.emptyState}>
                Nenhum produto encontrado.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};

export default Home;
